// src/pages/Settings/Withdraw.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import API from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const Withdraw = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    accountNumber: '',
    accountHolderName: '',
    ifscCode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userBalance, setUserBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [withdrawalSettings, setWithdrawalSettings] = useState({
    minAmount: 100,
    maxAmount: 100000,
    isWithdrawTimeOpen: true,
    withdrawOpenTime: '00:00',
    withdrawCloseTime: '23:59'
  });

  // Fetch user's current balance and withdrawal settings
  useEffect(() => {
    const fetchBalanceAndSettings = async () => {
      try {
        setLoading(true);
        const response = await API.get('/admin/withdraw/balance');
        
        if (response.data.success) {
          setUserBalance(response.data.data.balance);
          setWithdrawalSettings({
            minAmount: response.data.data.withdrawalSettings.minAmount,
            maxAmount: response.data.data.withdrawalSettings.maxAmount,
            isWithdrawTimeOpen: response.data.data.withdrawalSettings.isWithdrawTimeOpen,
            withdrawOpenTime: response.data.data.withdrawalSettings.withdrawOpenTime,
            withdrawCloseTime: response.data.data.withdrawalSettings.withdrawCloseTime
          });
        }
      } catch (error) {
        console.error('Error fetching balance:', error);
        // Fallback to localStorage if API fails
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser?.user) {
          setUserBalance(storedUser.user.wallet_balance || 0);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBalanceAndSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBankDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value) || 0;
      if (numValue <= userBalance) {
        setAmount(value);
      } else if (numValue > userBalance) {
        Swal.fire({
          icon: 'warning',
          title: 'Insufficient Balance',
          text: `You only have ${userBalance} points available`,
          confirmButtonColor: '#f7a90b'
        });
      }
    }
  };

  const validateForm = () => {
    if (!amount || parseInt(amount) <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Amount',
        text: 'Please enter a valid amount greater than 0',
        confirmButtonColor: '#f7a90b'
      });
      return false;
    }

    if (parseInt(amount) < withdrawalSettings.minAmount) {
      Swal.fire({
        icon: 'error',
        title: 'Minimum Amount',
        text: `Minimum withdrawal amount is ${withdrawalSettings.minAmount} points`,
        confirmButtonColor: '#f7a90b'
      });
      return false;
    }

    if (parseInt(amount) > withdrawalSettings.maxAmount) {
      Swal.fire({
        icon: 'error',
        title: 'Maximum Amount',
        text: `Maximum withdrawal amount is ${withdrawalSettings.maxAmount} points`,
        confirmButtonColor: '#f7a90b'
      });
      return false;
    }

    if (parseInt(amount) > userBalance) {
      Swal.fire({
        icon: 'error',
        title: 'Insufficient Balance',
        text: `You cannot withdraw more than your available balance of ${userBalance} points`,
        confirmButtonColor: '#f7a90b'
      });
      return false;
    }

    if (!withdrawalSettings.isWithdrawTimeOpen) {
      Swal.fire({
        icon: 'warning',
        title: 'Withdrawal Time Closed',
        text: `Withdrawal requests are only accepted between ${withdrawalSettings.withdrawOpenTime} and ${withdrawalSettings.withdrawCloseTime}`,
        confirmButtonColor: '#f7a90b'
      });
      return false;
    }

    // if (!bankDetails.bankName.trim()) {
    //   Swal.fire({
    //     icon: 'error',
    //     title: 'Bank Name Required',
    //     text: 'Please enter your bank name',
    //     confirmButtonColor: '#f7a90b'
    //   });
    //   return false;
    // }

    // if (!bankDetails.accountNumber.trim()) {
    //   Swal.fire({
    //     icon: 'error',
    //     title: 'Account Number Required',
    //     text: 'Please enter your account number',
    //     confirmButtonColor: '#f7a90b'
    //   });
    //   return false;
    // }

    // if (!bankDetails.accountHolderName.trim()) {
    //   Swal.fire({
    //     icon: 'error',
    //     title: 'Account Holder Name Required',
    //     text: 'Please enter the account holder name',
    //     confirmButtonColor: '#f7a90b'
    //   });
    //   return false;
    // }

    // if (!bankDetails.ifscCode.trim()) {
    //   Swal.fire({
    //     icon: 'error',
    //     title: 'IFSC Code Required',
    //     text: 'Please enter your IFSC code',
    //     confirmButtonColor: '#f7a90b'
    //   });
    //   return false;
    // }

    // // Basic IFSC validation (11 characters, alphanumeric)
    // if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankDetails.ifscCode.toUpperCase())) {
    //   Swal.fire({
    //     icon: 'error',
    //     title: 'Invalid IFSC Code',
    //     text: 'Please enter a valid IFSC code (e.g., SBIN0012345)',
    //     confirmButtonColor: '#f7a90b'
    //   });
    //   return false;
    // }

    return true;
  };

  const handleWithdraw = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await API.post('/admin/withdraw/request', {
        amount: parseInt(amount),
        bankDetails: {
          bankName: bankDetails.bankName,
          accountNumber: bankDetails.accountNumber,
          accountHolderName: bankDetails.accountHolderName,
          ifscCode: bankDetails.ifscCode.toUpperCase()
        }
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Withdrawal Request Submitted!',
          text: `Your request for ${amount} points has been submitted successfully. Please wait for admin approval.`,
          confirmButtonColor: '#f7a90b'
        });

        // Clear form
        setAmount('');
        setBankDetails({
          bankName: '',
          accountNumber: '',
          accountHolderName: '',
          ifscCode: ''
        });

        // Update balance from response
        if (response.data.data?.remainingBalance !== undefined) {
          setUserBalance(response.data.data.remainingBalance);
          
          // Update localStorage
          const storedUser = JSON.parse(localStorage.getItem('user'));
          if (storedUser?.user) {
            storedUser.user.wallet_balance = response.data.data.remainingBalance;
            localStorage.setItem('user', JSON.stringify(storedUser));
          }
        }

        // Refresh user data
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Withdrawal Failed',
        text: error.response?.data?.error || 'Failed to submit withdrawal request. Please try again.',
        confirmButtonColor: '#f7a90b'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="main-page">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">Withdraw Points</h1>
        </div>
        <div className="admin-card-one">
          <div className="admin-card-header">
            <h2 className="admin-card-heading">Withdrawal Request</h2>
            <p className="text-muted mt-1 mb-0" style={{ color: '#fff7ca !important', fontSize: '14px' }}>
              Request to withdraw your points to your bank account
            </p>
          </div>
          <div className="admin-card-body">
            {/* Balance Display */}
            <div className="mb-4 p-3 rounded" style={{ 
              background: 'linear-gradient(135deg, #2d0b2d, #1a041a)',
              border: '1px solid #f7a90b',
              borderRadius: '12px'
            }}>
              <div className="row align-items-center">
                <div className="col-sm-6">
                  <label className="admin-label-one mb-0">Available Balance</label>
                  <h2 className="text-warning mt-2 mb-0" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    {userBalance.toLocaleString()} Points
                  </h2>
                </div>
                <div className="col-sm-6 text-sm-end mt-3 mt-sm-0">
                  <small style={{ color: '#fff7ca' }}>
                    Min: {withdrawalSettings.minAmount} | Max: {withdrawalSettings.maxAmount} points
                  </small>
                </div>
              </div>
            </div>

            {/* Withdrawal Time Warning */}
            {!withdrawalSettings.isWithdrawTimeOpen && (
              <div className="alert alert-warning mb-4" style={{ 
                background: 'rgba(247, 169, 11, 0.2)', 
                border: '1px solid #f7a90b',
                borderRadius: '8px',
                color: '#fff7ca'
              }}>
                <i className="fas fa-clock me-2"></i>
                <strong>Withdrawal Time:</strong> {withdrawalSettings.withdrawOpenTime} - {withdrawalSettings.withdrawCloseTime}
                <br />
                <small>Withdrawal requests are currently closed. Please try again during operating hours.</small>
              </div>
            )}

            {/* Withdrawal Form */}
            <form onSubmit={(e) => { e.preventDefault(); handleWithdraw(); }}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="admin-label-one">Withdrawal Points</label>
                  <input
                    type="text"
                    className="admin-input-one"
                    placeholder={`Enter Points to withdraw (${withdrawalSettings.minAmount} - ${withdrawalSettings.maxAmount})`}
                    value={amount}
                    onChange={handleAmountChange}
                    disabled={isSubmitting || !withdrawalSettings.isWithdrawTimeOpen}
                  />
                </div>

                {/* <div className="col-12">
                  <label className="admin-label-one">Bank Name</label>
                  <input
                    type="text"
                    name="bankName"
                    className="admin-input-one"
                    placeholder="e.g., State Bank of India"
                    value={bankDetails.bankName}
                    onChange={handleInputChange}
                    disabled={isSubmitting || !withdrawalSettings.isWithdrawTimeOpen}
                  />
                </div>

                <div className="col-12">
                  <label className="admin-label-one">Account Number</label>
                  <input
                    type="text"
                    name="accountNumber"
                    className="admin-input-one"
                    placeholder="Enter your bank account number"
                    value={bankDetails.accountNumber}
                    onChange={handleInputChange}
                    disabled={isSubmitting || !withdrawalSettings.isWithdrawTimeOpen}
                  />
                </div>

                <div className="col-12">
                  <label className="admin-label-one">Account Holder Name</label>
                  <input
                    type="text"
                    name="accountHolderName"
                    className="admin-input-one"
                    placeholder="Name as per bank account"
                    value={bankDetails.accountHolderName}
                    onChange={handleInputChange}
                    disabled={isSubmitting || !withdrawalSettings.isWithdrawTimeOpen}
                  />
                </div>

                <div className="col-12 mb-3">
                  <label className="admin-label-one">IFSC Code</label>
                  <input
                    type="text"
                    name="ifscCode"
                    className="admin-input-one"
                    placeholder="e.g., SBIN0012345"
                    value={bankDetails.ifscCode}
                    onChange={handleInputChange}
                    disabled={isSubmitting || !withdrawalSettings.isWithdrawTimeOpen}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div> */}

                <div className="col-12">
                  <div className="alert alert-info mb-3" style={{ 
                    background: 'rgba(247, 169, 11, 0.1)', 
                    border: '1px solid #f7a90b',
                    borderRadius: '8px',
                    color: '#fff7ca',
                    padding: '12px'
                  }}>
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Note:</strong> Withdrawal requests will be processed within 24-48 hours after admin approval.
                  </div>
                </div>

                <div className="col-12">
                  <button 
                    type="submit" 
                    className="admin-submit-one w-100" 
                    disabled={isSubmitting || !amount || parseInt(amount) < withdrawalSettings.minAmount || !withdrawalSettings.isWithdrawTimeOpen}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Submitting...
                      </>
                    ) : (
                      'Submit Withdrawal Request'
                    )}
                  </button>
                </div>

                <div className="col-12 text-center mt-3">
                  <button 
                    type="button"
                    className="btn btn-link" 
                    onClick={() => navigate('/withdrawals/history')}
                    style={{ color: '#f7a90b', textDecoration: 'none' }}
                  >
                    View Withdrawal History →
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Withdraw;